import React, { useState, useMemo } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import { GetAll } from "adminApi/ReturnInvoiceApi/ReturnInvoice";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/ReturnInvoiceFeilds.json";

const ReturnInvoiceListTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
  const sizeOptions = [10, 20, 50, 100];
  const { tabledata } = fields;

  // Fetch Hook
  const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAll, {
    page,
    limit,
    search: debouncedSearch,
    title: "Return Invoice",
  });

  const displayFields = generateDisplayFields(tabledata);

  // Memoized: format return_date from raw MySQL datetime to DD/MM/YYYY
  // Guarded against null/undefined/unexpected formats
  const formattedData = useMemo(() => {
    return data.map((item) => {
      let formattedDate = item.return_date || "-";

      if (item.return_date) {
        try {
          // Raw MySQL datetime: "2026-04-15T00:00:00.000Z" or "2026-04-15"
          const datePart = item.return_date.split("T")[0];
          const parts = datePart?.split("-");
          if (parts?.length === 3) {
            const [year, month, day] = parts;
            formattedDate = `${day}/${month}/${year}`;
          }
        } catch {
          formattedDate = item.return_date;
        }
      }

      return { ...item, return_date: formattedDate };
    });
  }, [data]);

  // Table Hook
  const { formattedData: tableRows, columns } = useTableConfig({
    data: formattedData,
    page,
    limit,
    canShowAction,
    refetch,
    IsDelete: false,
    IsEdit: false,
    isView: true,
    config: {
      entityName: "Return Invoice",
      idKey: "purchase_return_id",
      displayFields,
      ViewUrl: "view-return-invoice",
    },
  });

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const onPageChange = (p) => setPage(p);
  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={tableRows}
      options={{
        loading,
        error,
        sortableColumns: displayFields.map((f) => f.accessor),
        copy: true,
        csv: true,
        pdf: true,
        print: true,
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch: handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default ReturnInvoiceListTable;
